import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { getDealUpdateReportForDeal } from "@/lib/deal-update.functions";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute(
  "/_authed/deals/$dealId_/deal-update/$reportId",
)({
  head: () => ({
    meta: [
      { title: "Deal Update Report: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealUpdateReportPage,
});

/* Defensive readers: report_json is model assembled. */
type Dict = Record<string, unknown>;

function asDict(v: unknown): Dict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Dict) : null;
}
function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function asDicts(v: unknown): Dict[] {
  return asArray(v)
    .map(asDict)
    .filter((d): d is Dict => !!d);
}
function asStr(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}
function asNum(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function strList(v: unknown): string[] {
  return asArray(v)
    .map(asStr)
    .filter((s): s is string => !!s);
}

function Section({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-brand-line bg-brand-paper p-5">
      <h2 className="font-serif text-lg text-brand-ink">
        {index}. {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-lg border border-brand-accent/30 bg-brand-accent/5 p-4 text-sm text-brand-ink">
      {children}
    </div>
  );
}

function Table({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number | null)[][];
}) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead>
          <tr className="border-b border-brand-line">
            {headers.map((h) => (
              <th key={h} className="py-2 pr-3 text-xs font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-brand-line/60 align-top">
              {r.map((c, j) => (
                <td key={j} className="py-2 pr-3 text-brand-ink">
                  {c ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <>
      {items.map((p, i) => (
        <p key={i} className="mt-2 text-sm text-brand-ink">
          {p}
        </p>
      ))}
    </>
  );
}

function Bullets({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-brand-ink">
      {items.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ul>
  );
}

const PACE_LABEL: Record<string, string> = {
  on_pace: "On pace",
  behind: "Behind pace",
  far_behind: "Far behind pace",
  not_started: "Not started",
};

const RECOMMENDATION_LABEL: Record<string, string> = {
  yes: "Yes, expand now",
  not_yet: "Not yet",
  adjust_criteria_first: "Adjust criteria first",
};

function DealUpdateReportPage() {
  const { dealId, reportId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchReport = useServerFn(getDealUpdateReportForDeal);
  const fetchDeal = useServerFn(getDealDetail);

  const reportQuery = useQuery({
    queryKey: ["deal-update-report", dealId, reportId],
    queryFn: () => fetchReport({ data: { dealId, reportId } }),
    enabled: !!user,
  });

  const dealQuery = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDeal({ data: { dealId } }),
    enabled: !!user,
  });

  const row = (reportQuery.data?.report ?? null) as Dict | null;
  const status = asStr(row?.["report_status"]);

  useEffect(() => {
    if (status === "processing") {
      navigate({ to: "/deals/$dealId", params: { dealId } });
    }
  }, [status, dealId, navigate]);

  useEffect(() => {
    document.body.classList.add("model-review-print");
    return () => {
      document.body.classList.remove("model-review-print");
    };
  }, []);

  if (reportQuery.isLoading || dealQuery.isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10 text-sm text-muted-foreground">
        Loading the report...
      </div>
    );
  }

  if (reportQuery.isError || !row) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <p className="text-sm text-brand-ink">This report is not available.</p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  const dealName =
    asStr((dealQuery.data as { deal?: { name?: string } } | undefined)?.deal?.name) ??
    "Deal";
  const pageTitle = `${dealName} Deal Update Report`;

  const handleDownloadPdf = () => {
    const originalTitle = document.title;
    document.title = pageTitle;
    const restore = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    setTimeout(() => {
      if (document.title === pageTitle) {
        document.title = originalTitle;
        window.removeEventListener("afterprint", restore);
      }
    }, 1000);
    window.print();
  };

  if (status === "failed") {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-10">
        <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
        <p className="text-sm text-brand-ink">
          This report did not finish. Any credits charged were returned.
        </p>
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-sm underline underline-offset-2"
        >
          Back to the deal
        </Link>
      </div>
    );
  }

  if (status === "processing") return null;

  const report = asDict(row["report_json"]) ?? {};
  const header = asDict(report["header"]) ?? {};
  const s = asDict(report["sections"]) ?? {};
  const disclaimer = asStr(report["disclaimer"]);

  /* 1 */
  const stands = asDict(s["where_your_raise_stands_now"]) ?? {};
  const pace = asDict(stands["pace"]) ?? {};
  const delivery = asDict(stands["delivery"]) ?? {};

  /* 2 */
  const movement = asDict(s["status_movement"]) ?? {};
  const statusCounts = asDicts(movement["status_counts"]);
  const priorDelta = asDict(movement["prior_delta"]);
  const deltas = asDicts(priorDelta?.["deltas"]);
  const emptyNote = asStr(movement["empty_state_note"]);

  /* 3 */
  const engagement = asDict(s["engagement_read"]) ?? {};
  const engagementAvailable = engagement["available"] === true;
  const engagementByType = asDicts(engagement["by_type"]);

  /* 4 */
  const priorities = asDict(s["priorities_and_next_actions"]) ?? {};
  const priorityContacts = asDicts(priorities["contacts"]);
  const restartPlan = asStr(priorities["restart_plan"]);

  /* 5 */
  const stall = asDict(s["stall_points_and_reset"]) ?? {};

  /* 6 */
  const expansion = asDict(s["expansion_call"]) ?? {};
  const recommendation = asStr(expansion["recommendation"]);

  /* 7 */
  const twoWeek = asDicts(s["two_week_plan"]);

  return (
    <div className="model-review-report mx-auto max-w-4xl space-y-5 px-4 py-8">
      <style>{`
        @media print {
          @page { margin: 0.75in; }
          body.model-review-print {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.model-review-print header.sticky { display: none !important; }
          .model-review-report .no-print { display: none !important; }
          .model-review-report {
            max-width: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .model-review-report section,
          .model-review-report .rounded-lg {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0.5rem 0 !important;
            margin: 1rem 0 !important;
          }
          .model-review-report h1,
          .model-review-report h2,
          .model-review-report h3 {
            color: #000 !important;
            break-after: avoid;
          }
          .model-review-report .font-serif { color: #000 !important; }
          .model-review-report p,
          .model-review-report li,
          .model-review-report td,
          .model-review-report th { color: #000 !important; }
          .model-review-report .text-muted-foreground,
          .model-review-report .text-brand-muted { color: #333 !important; }
          .model-review-report .overflow-x-auto {
            overflow: visible !important;
            width: 100% !important;
          }
          .model-review-report table {
            width: 100% !important;
            min-width: 0 !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
          }
          .model-review-report thead { display: table-row-group !important; }
          .model-review-report tr {
            border-top: 1px solid #ccc !important;
            border-bottom: 1px solid #ccc !important;
            page-break-inside: auto !important;
          }
          .model-review-report td,
          .model-review-report th {
            padding: 0.35rem 0.5rem 0.35rem 0 !important;
            vertical-align: top !important;
            border: none !important;
          }
          .model-review-report .border-brand-line { border-color: #ccc !important; }
        }
      `}</style>

      <div className="no-print space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-brand-ink">{pageTitle}</h1>
          {asStr(header["raise_summary_line"]) && (
            <p className="mt-1 text-sm text-brand-muted">
              {asStr(header["raise_summary_line"])}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {asStr(header["prepared_date"])
              ? `Prepared ${new Date(String(header["prepared_date"])).toLocaleDateString()}`
              : ""}
            {asNum(header["contact_count"]) != null
              ? ` · ${asNum(header["contact_count"])} contacts`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 no-print">
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Download PDF
          </button>
          <Link
            to="/deals/$dealId"
            params={{ dealId }}
            className="shrink-0 text-sm underline underline-offset-2"
          >
            Back to the deal
          </Link>
        </div>
      </div>

      {/* 1 */}
      <Section index={1} title="Where Your Raise Stands Now">
        {asStr(stands["momentum_read"]) && (
          <p className="text-sm font-medium text-brand-ink">
            {asStr(stands["momentum_read"])}
          </p>
        )}
        <Paragraphs items={strList(stands["paragraphs"])} />
        <div className="mt-3 space-y-1 text-sm text-brand-ink">
          {asNum(header["contact_count"]) != null && (
            <p>
              <span className="font-medium">Contacts: </span>
              {asNum(header["contact_count"])}
            </p>
          )}
          {asNum(delivery["days_since_first_delivery"]) != null && (
            <p>
              <span className="font-medium">Days since first delivery: </span>
              {asNum(delivery["days_since_first_delivery"])}
            </p>
          )}
          {asNum(pace["expected_contacted_by_now"]) != null && (
            <p>
              <span className="font-medium">Expected contacted by now: </span>
              {asNum(pace["expected_contacted_by_now"])}
            </p>
          )}
          {asNum(pace["actual_contacted"]) != null && (
            <p>
              <span className="font-medium">Actual contacted: </span>
              {asNum(pace["actual_contacted"])}
            </p>
          )}
          {asStr(pace["read"]) && (
            <p>
              <span className="font-medium">Pace: </span>
              {PACE_LABEL[String(pace["read"])] ?? String(pace["read"])}
            </p>
          )}
        </div>
      </Section>

      {/* 2 */}
      <Section index={2} title="Status Movement">
        {statusCounts.length > 0 && (
          <Table
            headers={["Status", "Contacts"]}
            rows={statusCounts.map((c) => [
              asStr(c["investor_status"]) ?? "Unknown",
              asNum(c["contacts"]) ?? 0,
            ])}
          />
        )}
        {deltas.length > 0 && (
          <>
            <p className="mt-4 text-sm text-brand-ink">
              Compared with your prior report
              {asStr(priorDelta?.["prior_report_date"])
                ? ` from ${new Date(String(priorDelta?.["prior_report_date"])).toLocaleDateString()}`
                : ""}
              .
            </p>
            <Table
              headers={["Status", "Prior", "Current", "Change"]}
              rows={deltas.map((d) => [
                asStr(d["investor_status"]) ?? "Unknown",
                asNum(d["prior"]) ?? 0,
                asNum(d["current"]) ?? 0,
                asNum(d["change"]) ?? 0,
              ])}
            />
          </>
        )}
        <Paragraphs items={strList(movement["narrative"])} />
        {emptyNote && <Callout>{emptyNote}</Callout>}
      </Section>

      {/* 3 */}
      <Section index={3} title="Engagement by Investor Type">
        {asStr(engagement["summary"]) && (
          <p className="text-sm text-brand-ink">{asStr(engagement["summary"])}</p>
        )}
        {engagementAvailable && engagementByType.length > 0 && (
          <Table
            headers={["Investor Type", "Read", "What To Do"]}
            rows={engagementByType.map((r) => [
              asStr(r["investor_type"]) ?? "",
              asStr(r["read"]) ?? "",
              asStr(r["what_to_do"]) ?? "",
            ])}
          />
        )}
      </Section>

      {/* 4 */}
      <Section index={4} title="Priorities and Next Actions">
        {priorityContacts.length > 0 ? (
          <div className="space-y-3">
            {priorityContacts.map((c, i) => (
              <div
                key={i}
                className="rounded-lg border border-brand-line bg-brand-paper-2 p-4"
              >
                <p className="text-sm font-medium text-brand-ink">
                  {asStr(c["name"]) ?? "Contact"}
                  {asStr(c["firm"]) ? `, ${asStr(c["firm"])}` : ""}
                </p>
                {asStr(c["current_status"]) && (
                  <p className="mt-1 text-sm text-brand-ink">
                    <span className="font-medium">Current status: </span>
                    {asStr(c["current_status"])}
                  </p>
                )}
                {asStr(c["why_it_matters"]) && (
                  <p className="mt-1 text-sm text-brand-ink">
                    <span className="font-medium">Why it matters: </span>
                    {asStr(c["why_it_matters"])}
                  </p>
                )}
                {asStr(c["suggested_next_action"]) && (
                  <p className="mt-1 text-sm text-brand-ink">
                    <span className="font-medium">Suggested next action: </span>
                    {asStr(c["suggested_next_action"])}
                  </p>
                )}
                {asStr(c["channel"]) && (
                  <p className="mt-1 text-sm text-brand-ink">
                    <span className="font-medium">Channel: </span>
                    {asStr(c["channel"])}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          restartPlan && <Callout>{restartPlan}</Callout>
        )}
      </Section>

      {/* 5 */}
      <Section index={5} title="Stall Points and Reset">
        {asStr(stall["where_it_stands"]) && (
          <p className="text-sm text-brand-ink">{asStr(stall["where_it_stands"])}</p>
        )}
        <Bullets items={strList(stall["stall_points"])} />
        {strList(stall["reset_this_week"]).length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-muted-foreground">This week</p>
            <Bullets items={strList(stall["reset_this_week"])} />
          </div>
        )}
      </Section>

      {/* 6 */}
      <Section index={6} title="Expansion Call">
        {recommendation && (
          <p className="text-sm font-medium text-brand-ink">
            Recommendation: {RECOMMENDATION_LABEL[recommendation] ?? recommendation}
          </p>
        )}
        {asStr(expansion["rationale"]) && (
          <p className="mt-2 text-sm text-brand-ink">
            {asStr(expansion["rationale"])}
          </p>
        )}
        {strList(expansion["stop_conditions"]).length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-muted-foreground">
              Stop conditions
            </p>
            <Bullets items={strList(expansion["stop_conditions"])} />
          </div>
        )}
      </Section>

      {/* 7 */}
      <Section index={7} title="Two Week Plan">
        {twoWeek.length > 0 && (
          <Table
            headers={["Week", "Send Block", "Channels", "In Parallel", "Output"]}
            rows={twoWeek.map((w) => [
              asStr(w["week"]) ?? "",
              asStr(w["send_block"]) ?? "",
              asStr(w["channels"]) ?? "",
              asStr(w["in_parallel"]) ?? "",
              asStr(w["output"]) ?? "",
            ])}
          />
        )}
      </Section>

      {(() => {
        const plan = asDict(s["plan_check"]);
        if (!plan) return null;
        const rate = asNum(plan["reply_rate"]);
        return (
          <Section index={8} title="Plan Check">
            <div className="space-y-1 text-sm text-brand-ink">
              <p>
                {`${asStr(plan["phase"]) === "launch" ? "Launch" : "Follow-up"} phase: ${asNum(plan["count"]) ?? 0} of ${asNum(plan["target"]) ?? 0} ${asStr(plan["unit"]) ?? ""}`}
                {asStr(plan["deadline"]) ? ` by ${asStr(plan["deadline"])}` : ""}
                {asStr(plan["pace"]) ? `, ${String(asStr(plan["pace"])).replace("_", " ")}` : ""}
              </p>
              <p>
                {`This week: ${asNum(plan["sent_this_week"]) ?? 0} sent, ${asNum(plan["replies_this_week"]) ?? 0} replies, a reply rate of ${rate == null ? 0 : Math.round(rate * 1000) / 10} percent.`}
              </p>
              <p className="font-medium">
                {`Recommendation: ${asStr(plan["recommendation"]) ?? "Hold the current plan"}.`}
              </p>
            </div>
          </Section>
        );
      })()}


      {disclaimer && (
        <p className="text-xs text-muted-foreground">{disclaimer}</p>
      )}
    </div>
  );
}
